import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { MessageCircle, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

export default function FAQSection({ onBookingOpen }) {
  const { t } = useLanguage();
  const section = t("faq");
  const items = t("practiceAreas.items") || [];
  const noAnswer = section.noAnswer || {};

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 bg-[#f5f0e8]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="text-center mb-16">
          <p className="text-[#1a5c4a] text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{section.subtitle}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a1a2e] mb-4 tracking-[-0.02em]">{section.headline}</h2>
        </Reveal>

        <Reveal>
          {items.length > 0 && (
            <Tabs defaultValue={items[0].id} className="w-full" data-testid="faq-tabs">
              <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent p-0 mb-10 justify-center">
                {items.map((area) => (
                  <TabsTrigger
                    key={area.id}
                    value={area.id}
                    data-testid={`faq-tab-${area.id}`}
                    className="rounded-full border border-[#d5cfc6] text-[#4a4540] px-5 py-2 text-xs font-medium data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white data-[state=active]:border-[#1a1a2e] transition-all"
                  >
                    {area.title}
                  </TabsTrigger>
                ))}
              </TabsList>

              {items.map((area) => (
                <TabsContent key={area.id} value={area.id} data-testid={`faq-content-${area.id}`}>
                  <Accordion type="single" collapsible className="w-full">
                    {area.faq.map((item, i) => (
                      <AccordionItem key={`${area.id}-faq-${i}`} value={`${area.id}-${i}`} className="border-[#e0d9ce]">
                        <AccordionTrigger className="text-left text-[#1a1a2e] hover:text-[#1a5c4a] hover:no-underline text-base font-medium">
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-[#4a4540] leading-relaxed">{item.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </Reveal>

        <Reveal animation="reveal-scale" className="mt-14">
          <div className="text-center p-8 bg-white rounded-2xl border border-[#e8e3db]">
            <MessageCircle className="h-8 w-8 text-[#1a5c4a] mx-auto mb-3" />
            <h3 className="font-heading text-xl font-bold text-[#1a1a2e] mb-2">{noAnswer.title}</h3>
            <p className="text-[#4a4540] text-sm mb-5">{noAnswer.desc}</p>
            <Button
              data-testid="faq-contact-cta"
              onClick={scrollToContact}
              className="bg-[#1a5c4a] text-white hover:bg-[#154a3c] rounded-full px-8 py-5 font-semibold transition-all duration-300 hover:-translate-y-0.5"
            >
              {noAnswer.cta} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
